import { ReactNode, useEffect, useState } from 'react';
import { useMatch, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { applyPalette, resolvePalette } from '../lib/theme';
import FloatingButtons from './FloatingButtons';
import GlobalSearch from './GlobalSearch';
import IconSidebar from './IconSidebar';
import NavPanel from './NavPanel';
import QuickNoteFab from './QuickNoteFab';

export default function AppLayout({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [searchOpen, setSearchOpen] = useState(false);
  const [focusOn, setFocusOn] = useState(false);

  // Applique la palette d'ambiance sur :root (CSS vars) au chargement et à chaque changement
  useEffect(() => {
    applyPalette(resolvePalette(user?.mood));
  }, [user?.mood]);

  // Mode focus : masque sidebars + nav projet + onglets
  useEffect(() => {
    if (focusOn) document.body.classList.add('focus-mode');
    else document.body.classList.remove('focus-mode');
    return () => document.body.classList.remove('focus-mode');
  }, [focusOn]);

  // Esc pour quitter le mode focus
  useEffect(() => {
    if (!focusOn) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFocusOn(false);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [focusOn]);

  // Raccourci clavier Cmd/Ctrl+K pour recherche
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  return (
    <div className="app-shell">
      <IconSidebar
        onSearch={() => setSearchOpen(true)}
        onToggleFocus={() => setFocusOn((v) => !v)}
        focusOn={focusOn}
      />
      <NavPanel />
      <ProjectThemeBridge />
      <main className="main">{children}</main>
      <FloatingButtons />
      <QuickNoteFab />
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      {focusOn && (
        <button
          type="button"
          className="focus-exit"
          onClick={() => setFocusOn(false)}
          title="Quitter le mode focus (Esc)"
        >
          × Quitter le focus
        </button>
      )}
    </div>
  );
}

/** Applique la couleur thème du projet actif sur la variable --c-project. */
function ProjectThemeBridge() {
  const match = useMatch('/projets/:id/*');
  const params = useParams<{ id?: string }>();
  const projectId = match ? Number(params.id ?? '0') : null;

  useEffect(() => {
    if (!projectId) {
      document.documentElement.style.removeProperty('--c-project');
      return;
    }
    api
      .get<{ project: { theme_color: string } }>(`/projects/${projectId}`)
      .then((r) => {
        document.documentElement.style.setProperty(
          '--c-project',
          r.project.theme_color || '#81B29A'
        );
      })
      .catch(() => {
        document.documentElement.style.removeProperty('--c-project');
      });
  }, [projectId]);

  return null;
}
